import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiFile, FiFilm, FiPlus, FiUploadCloud, FiX, FiAlertTriangle, FiEye } from 'react-icons/fi';
import { Button } from '../common/Button';
import { SweepSlot } from '../SweepSlot/SweepSlot';

export const FileWorkspace = ({ activeTab, setActiveTab, obFiles, onRemoveOb, blindAssignments, onRemoveBlind, onBlindDrop, obInputRef, blindInputRef, targetPatientId, setPreviewItem, SWEEPS, onBlindClick }: any) => {
  const { t } = useTranslation('NewCaseWizard');

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow  dark:border-gray-700 flex flex-col h-full overflow-hidden transition-colors">
      
      {/* HEADER SECTION */}
      <div className="flex border-b dark:border-gray-700 justify-between items-center pr-4">
        <div className="flex">
          <button onClick={() => setActiveTab('OB')} className={`px-6 py-4 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'OB' ? 'border-blue-500 text-blue-600 bg-blue-50/50 dark:bg-blue-900/20 dark:text-blue-400' : 'border-transparent text-slate-400 dark:text-gray-500'}`}><FiFile/> {t('oneStep.workspace.obScan')}</button>
          <button onClick={() => setActiveTab('BLIND')} className={`px-6 py-4 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'BLIND' ? 'border-purple-500 text-purple-600 bg-purple-50/50 dark:bg-purple-900/20 dark:text-purple-400' : 'border-transparent text-slate-400 dark:text-gray-500'}`}><FiFilm/> {t('oneStep.workspace.blindSweep')}</button>
        </div>
        <div className="flex gap-2 items-center">
          <Button size="sm" onClick={() => obInputRef.current?.click()} className="bg-blue-600 text-white shadow-sm"><FiPlus/> {t('oneStep.workspace.addOb')}</Button>
          <Button size="sm" onClick={() => blindInputRef.current?.click()} className="bg-purple-600 text-white shadow-sm"><FiPlus/> {t('oneStep.workspace.addBlind')}</Button>
        </div>
      </div>

      {/* CONTENT AREA - HEIGHT FIXED HERE */}
      <div className="flex-1 overflow-y-auto min-h-[450px] lg:min-h-0 lg:h-full p-6 bg-slate-50 dark:bg-gray-900 relative flex flex-col transition-colors">
        
        {activeTab === 'OB' && (
          obFiles.length === 0 ? (
            /* EMPTY STATE BOX - FORCED TO FILL HEIGHT */
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 dark:text-gray-500 border-2 border-dashed border-slate-300 dark:border-gray-700 rounded-xl bg-slate-50/50 dark:bg-gray-800/30 h-full min-h-[350px]">
              <FiUploadCloud className="text-5xl mb-4 opacity-40"/>
              <span className="font-semibold text-lg">{t('oneStep.workspace.noObFilesYet')}</span>
              <Button size="sm" variant="ghost" className="mt-4 dark:text-gray-400 dark:hover:text-white border border-slate-200" onClick={() => obInputRef.current?.click()}>
                {t('oneStep.workspace.uploadNow')}
              </Button>
            </div>
          ) : (
            /* FILES GRID */
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {obFiles.map((f:any) => {
                const isMatch = targetPatientId && f.dicomData?.patientId === targetPatientId;
                const isConflict = targetPatientId && f.dicomData && f.dicomData.patientId !== targetPatientId;
                return (
                  <div key={f.id} className={`bg-white dark:bg-gray-800 p-3 rounded-xl border-2 relative group hover:shadow-md transition-all ${isMatch ? 'border-green-400 bg-green-50 dark:bg-green-900/20 dark:border-green-600' : (isConflict ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-600' : 'border-slate-100 dark:border-gray-700 shadow-sm')}`}>
                    <button onClick={() => onRemoveOb(f.id)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow opacity-0 group-hover:opacity-100"><FiX/></button>
                    <div className="flex flex-col items-center gap-2">
                      <div className={`w-10 h-10 rounded flex items-center justify-center ${f.type === 'dicom' ? 'bg-blue-50 text-blue-500 dark:bg-blue-900/20 dark:text-blue-400' : 'bg-pink-50 text-pink-500 dark:bg-pink-900/20 dark:text-pink-400'}`}>{f.type === 'dicom' ? <FiFile/> : <FiFilm/>}</div>
                      <div className="text-xs font-medium truncate w-full text-center dark:text-gray-200">{f.file.name}</div>
                      {f.dicomData && <div className="text-[9px] text-slate-500 dark:text-gray-400 text-center font-mono">{f.dicomData.patientId}</div>}
                      {isConflict && <div className="text-[9px] text-amber-600 dark:text-amber-400 font-bold flex items-center"><FiAlertTriangle className="mr-1"/> {t('oneStep.workspace.idMismatch')}</div>}
                      <Button size="sm" className="w-full" onClick={() => setPreviewItem(f)}><FiEye/> {t('oneStep.workspace.preview')}</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {activeTab === 'BLIND' && (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-3 content-start">
            {SWEEPS.map((sweep: any) => (
              <SweepSlot key={sweep.id} sweep={sweep} fileItem={blindAssignments[sweep.id]} targetPatientId={targetPatientId} onDrop={(item:any) => onBlindDrop(item.sweepId, sweep.id)} onRemove={() => onRemoveBlind(sweep.id)} onPreview={() => setPreviewItem(blindAssignments[sweep.id])}  onClickSlot={() => {
        onBlindClick(sweep.id); 
    }}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};